import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  MapPin,
  Wind,
  Radar,
  Sparkles,
  AlertTriangle,
  Moon,
  Monitor,
  Palette,
  Navigation,
} from 'lucide-react';
import { useMapStore } from '@/stores/mapStore';
import { useWeatherStore } from '@/stores/weatherStore';
import { cn } from '@/lib/utils';

interface CommandItem {
  id: string;
  icon: React.ElementType;
  label: string;
  description?: string;
  category: 'location' | 'layer' | 'theme' | 'action';
  action: () => void;
  active?: boolean;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [geoResults, setGeoResults] = useState<CommandItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  const map = useMapStore((s) => s.map);
  const {
    windEnabled, setWindEnabled,
    radarEnabled, setRadarEnabled,
    lightPollutionEnabled, setLightPollutionEnabled,
    alertsEnabled, setAlertsEnabled,
  } = useWeatherStore();

  // ⌘K / Ctrl+K to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setGeoResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Geocode search via Nominatim
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setGeoResults([]);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(debouncedQuery)}`,
      { headers: { 'Accept-Language': 'en' } }
    )
      .then((r) => r.json())
      .then((results: any[]) => {
        if (cancelled) return;
        setGeoResults(
          results.map((r) => ({
            id: `geo-${r.place_id}`,
            icon: MapPin,
            label: r.display_name.split(',').slice(0, 2).join(','),
            description: r.display_name.split(',').slice(2, 4).join(',').trim(),
            category: 'location' as const,
            action: () => {
              if (!map) return;
              map.flyTo({
                center: [parseFloat(r.lon), parseFloat(r.lat)],
                zoom: r.type === 'country' ? 5 : r.type === 'state' ? 7 : 13,
                duration: 2000,
              });
              setOpen(false);
            },
          }))
        );
        setIsSearching(false);
      })
      .catch(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => { cancelled = true; };
  }, [debouncedQuery, map]);

  // Built-in commands
  const builtinCommands: CommandItem[] = [
    {
      id: 'toggle-wind',
      icon: Wind,
      label: `${windEnabled ? 'Hide' : 'Show'} Wind Layer`,
      category: 'layer',
      action: () => { setWindEnabled(!windEnabled); setOpen(false); },
      active: windEnabled,
    },
    {
      id: 'toggle-radar',
      icon: Radar,
      label: `${radarEnabled ? 'Hide' : 'Show'} Radar Layer`,
      category: 'layer',
      action: () => { setRadarEnabled(!radarEnabled); setOpen(false); },
      active: radarEnabled,
    },
    {
      id: 'toggle-light',
      icon: Sparkles,
      label: `${lightPollutionEnabled ? 'Hide' : 'Show'} Light Pollution`,
      category: 'layer',
      action: () => { setLightPollutionEnabled(!lightPollutionEnabled); setOpen(false); },
      active: lightPollutionEnabled,
    },
    {
      id: 'toggle-alerts',
      icon: AlertTriangle,
      label: `${alertsEnabled ? 'Hide' : 'Show'} Weather Alerts`,
      category: 'layer',
      action: () => { setAlertsEnabled(!alertsEnabled); setOpen(false); },
      active: alertsEnabled,
    },
    {
      id: 'theme-dark',
      icon: Monitor,
      label: 'Theme: Dark',
      category: 'theme',
      action: () => {
        document.documentElement.removeAttribute('data-theme');
        setOpen(false);
      },
    },
    {
      id: 'theme-midnight',
      icon: Moon,
      label: 'Theme: Midnight',
      category: 'theme',
      action: () => {
        document.documentElement.setAttribute('data-theme', 'midnight');
        setOpen(false);
      },
    },
    {
      id: 'theme-slate',
      icon: Palette,
      label: 'Theme: Slate',
      category: 'theme',
      action: () => {
        document.documentElement.setAttribute('data-theme', 'slate');
        setOpen(false);
      },
    },
    {
      id: 'goto-home',
      icon: Navigation,
      label: 'Go to Continental US',
      description: 'Default view',
      category: 'action',
      action: () => {
        map?.flyTo({ center: [-98.5, 39.5], zoom: 4, duration: 2000 });
        setOpen(false);
      },
    },
  ];

  // Filter commands by query
  const filteredCommands = query.length > 0
    ? builtinCommands.filter((c) =>
        c.label.toLowerCase().includes(query.toLowerCase())
      )
    : builtinCommands;

  const allItems = [...geoResults, ...filteredCommands];

  // Clamp selected index
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && allItems[selectedIndex]) {
        e.preventDefault();
        allItems[selectedIndex].action();
      }
    },
    [allItems, selectedIndex]
  );

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.children[selectedIndex] as HTMLElement;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Palette */}
      <div className="fixed inset-0 z-[101] flex items-start justify-center pt-[15vh] pointer-events-none">
        <div
          className="pointer-events-auto w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
          style={{ animation: 'cmdSlideIn 0.15s ease-out' }}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Search size={16} className="text-muted-foreground flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search locations, toggle layers, switch themes..."
              className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden sm:inline-flex h-5 items-center rounded border border-border bg-muted px-1.5 text-[10px] text-muted-foreground">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-80 overflow-y-auto p-1.5">
            {/* Location results */}
            {geoResults.length > 0 && (
              <>
                <div className="px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  Locations
                </div>
                {geoResults.map((item, i) => (
                  <CommandRow
                    key={item.id}
                    item={item}
                    selected={selectedIndex === i}
                    onSelect={item.action}
                    onHover={() => setSelectedIndex(i)}
                  />
                ))}
              </>
            )}

            {isSearching && query.length >= 2 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Searching...</div>
            )}

            {/* Commands */}
            {filteredCommands.length > 0 && (
              <>
                <div className="px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  Commands
                </div>
                {filteredCommands.map((item, i) => {
                  const idx = geoResults.length + i;
                  return (
                    <CommandRow
                      key={item.id}
                      item={item}
                      selected={selectedIndex === idx}
                      onSelect={item.action}
                      onHover={() => setSelectedIndex(idx)}
                    />
                  );
                })}
              </>
            )}

            {allItems.length === 0 && !isSearching && query.length > 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No results for "{query}"
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="flex items-center gap-3 px-4 py-2 border-t border-border text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1">↵</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1">esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cmdSlideIn {
          from { opacity: 0; transform: scale(0.98) translateY(-8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}

function CommandRow({
  item,
  selected,
  onSelect,
  onHover,
}: {
  item: CommandItem;
  selected: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      className={cn(
        'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left text-sm transition-colors',
        selected ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-accent/50'
      )}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <Icon size={15} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate">{item.label}</div>
        {item.description && (
          <div className="text-[11px] text-muted-foreground/70 truncate">{item.description}</div>
        )}
      </div>
      {item.active !== undefined && (
        <div className={cn(
          'w-2 h-2 rounded-full flex-shrink-0',
          item.active ? 'bg-emerald-400' : 'bg-muted-foreground/30'
        )} />
      )}
    </button>
  );
}
